import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Toaster } from './components/ui/sonner';
import Login from './pages/Login';
import PinLogin from './pages/PinLogin';
import './App.css';
import './styles/guestStatus.css';

// Lazy-load all route-level pages (only Login/PinLogin are eagerly loaded)
const Register = lazy(() => import('./pages/Register'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const ParticipantDashboard = lazy(() => import('./pages/ParticipantDashboard'));
const HeavenlyServiceCentreDashboard = lazy(() => import('./pages/HeavenlyServiceCentreDashboard'));
const AvatarCreationPage = lazy(() => import('./pages/AvatarCreationPage'));
const GuestSurveyPage = lazy(() => import('./pages/GuestSurveyPage'));
const SurveyResponsePage = lazy(() => import('./pages/SurveyResponsePage'));
const RoomDetailPage = lazy(() => import('./pages/RoomDetailPage'));
const EMSPresentationPage = lazy(() => import('./pages/EMSPresentationPage'));

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="w-10 h-10 border-3 border-cyan-400 border-t-transparent rounded-full animate-spin" />
  </div>
);

const RootRedirect = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  if (!user) {
    // Redirect to the public EMS marketing website
    window.location.href = '/ems-overview.html';
    return null;
  }

  // Route based on role
  if (user.role === 'heavenly_service_centre') {
    return <Navigate to="/service-centre" replace />;
  }

  if (user.role === 'participant') {
    return <Navigate to="/participant" replace />;
  }

  // Admin roles: general_manager, guest_service_manager, admin, engineer
  return <Navigate to="/admin" replace />;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/login" element={<Login />} />
          <Route path="/pin-login" element={<PinLogin />} />
          <Route path="/hsc" element={<Navigate to="/service-centre" replace />} />
          <Route path="/register" element={<Register />} />
          {/* Public survey page - no auth required */}
          <Route path="/survey/:token" element={<GuestSurveyPage />} />
          {/* 4 Key Steps Survey Response Pages - no auth required */}
          <Route path="/survey/step/:token" element={<SurveyResponsePage />} />
          <Route path="/survey/resolve/:token" element={<SurveyResponsePage />} />
          <Route path="/survey/resolve-step/:token" element={<SurveyResponsePage />} />
          <Route
            path="/avatar-creation"
            element={
              <ProtectedRoute>
                <AvatarCreationPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute adminOnly>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/participant"
            element={
              <ProtectedRoute>
                <ParticipantDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/service-centre"
            element={
              <ProtectedRoute>
                <HeavenlyServiceCentreDashboard />
              </ProtectedRoute>
            }
          />
          {/* Room Detail Page - Full page view */}
          <Route
            path="/room/:roomNumber"
            element={
              <ProtectedRoute>
                <RoomDetailPage />
              </ProtectedRoute>
            }
          />
          {/* EMS Marketing Presentation */}
          <Route
            path="/ems-presentation"
            element={
              <ProtectedRoute>
                <EMSPresentationPage />
              </ProtectedRoute>
            }
          />
        </Routes>
        </Suspense>
      </BrowserRouter>
      <Toaster />
    </AuthProvider>
  );
}

export default App;